import { useEffect, useRef, useState } from 'react';
import { MonitorPlay } from 'lucide-react';
import { io, type Socket } from 'socket.io-client';
import { OWNER_WATCH_NAMESPACE, WATCH_BATCH_INTERVAL_MS, WATCH_BATCH_MAX_BYTES, type OwnerWatchStartedEvent } from '@his/shared';
import { useAuth } from '@/lib/auth';
import { session } from '@/lib/api';
import { REALTIME_PATH, useRealtime } from '@/lib/realtime';

const BATCH_KEEP_HEADROOM = 0.9;

interface WatchInput {
  kind: 'click' | 'key';
  x?: number;
  y?: number;
  key?: string;
}

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function setNativeValue(element: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  const prototype = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  if (setter) setter.call(element, value);
  else element.value = value;
  element.dispatchEvent(new Event('input', { bubbles: true }));
}

export function applyRemoteInput(input: WatchInput, doc: Document = document): void {
  if (input.kind === 'click' && typeof input.x === 'number' && typeof input.y === 'number') {
    const target = doc.elementFromPoint(input.x, input.y);
    if (target instanceof HTMLElement) {
      target.focus();
      target.click();
    }
    return;
  }
  if (input.kind !== 'key' || typeof input.key !== 'string') return;
  const active = doc.activeElement;
  const editable = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
  if (editable && input.key.length === 1) {
    setNativeValue(active, active.value + input.key);
    return;
  }
  if (editable && input.key === 'Backspace') {
    setNativeValue(active, active.value.slice(0, -1));
    return;
  }
  active?.dispatchEvent(new KeyboardEvent('keydown', { key: input.key, bubbles: true }));
  if (input.key === 'Enter' && active instanceof HTMLElement) {
    const form = active.closest('form');
    if (form) form.requestSubmit();
    else active.click();
  }
}

export function OwnerWatchListener() {
  const { user } = useAuth();
  const [watch, setWatch] = useState<OwnerWatchStartedEvent | null>(null);
  const [controlRequest, setControlRequest] = useState<string | null>(null);
  const [controlled, setControlled] = useState(false);
  const socket = useRef<Socket | null>(null);
  const watchable = Boolean(user && !user.isPlatformUser && user.watchAllowed && !user.impersonation);
  const channels = watchable && user ? [`user:${user.id}`] : [];

  useRealtime(channels, (event, payload) => {
    if (event === 'owner-watch.start' && payload && typeof payload === 'object') setWatch(payload as OwnerWatchStartedEvent);
    if (event === 'owner-watch.end') setWatch(null);
  });

  useEffect(() => {
    const token = session.access();
    if (!watch || !token) return undefined;
    let stopRecording: (() => void) | undefined;
    let flushTimer: number | null = null;
    let buffer: unknown[] = [];
    let bufferedBytes = 0;
    let stopped = false;
    const link = io(OWNER_WATCH_NAMESPACE, { path: REALTIME_PATH, transports: ['websocket'], auth: { token, sessionId: watch.sessionId }, reconnectionDelay: 2_000, reconnectionDelayMax: 15_000 });
    socket.current = link;

    const flush = () => {
      if (buffer.length === 0 || !link.connected) return;
      link.emit('batch', buffer);
      buffer = [];
      bufferedBytes = 0;
    };
    const emit = (event: { type: number }) => {
      const size = JSON.stringify(event).length;
      if (size > WATCH_BATCH_MAX_BYTES * BATCH_KEEP_HEADROOM) return;
      if (bufferedBytes + size > WATCH_BATCH_MAX_BYTES * BATCH_KEEP_HEADROOM) flush();
      buffer.push(event);
      bufferedBytes += size;
    };
    const stop = () => {
      if (stopped) return;
      stopped = true;
      stopRecording?.();
      stopRecording = undefined;
      if (flushTimer !== null) window.clearInterval(flushTimer);
      flushTimer = null;
      buffer = [];
      setControlled(false);
      setControlRequest(null);
      setWatch(null);
    };

    link.on('ready', () => {
      void import('rrweb').then(({ record }) => {
        if (stopped) return;
        stopRecording = record({
          emit: (event) => emit(event),
          maskAllInputs: true,
          maskTextSelector: '[data-mask], .mask-for-owner',
          blockSelector: '[data-no-watch]',
          sampling: { mousemove: 150, scroll: 150, input: 'last' },
          inlineStylesheet: true,
        });
        flushTimer = window.setInterval(flush, WATCH_BATCH_INTERVAL_MS);
      });
    });
    link.on('peer', (payload: { side: string; present: boolean }) => {
      if (payload.side === 'viewer' && payload.present) void import('rrweb').then(({ record }) => (stopped ? undefined : record.takeFullSnapshot()));
    });
    link.on('control-request', (payload: { ownerName?: string }) => setControlRequest(payload?.ownerName ?? watch.ownerName));
    link.on('control', (payload: { granted: boolean }) => {
      setControlled(payload.granted);
      if (!payload.granted) setControlRequest(null);
    });
    link.on('input', (payload: WatchInput) => applyRemoteInput(payload));
    link.on('ended', stop);
    link.on('refused', stop);
    link.on('disconnect', () => {
      if (!stopped) flush();
    });
    return () => {
      stop();
      link.disconnect();
      socket.current = null;
    };
  }, [watch]);

  if (!watch) return null;

  return (
    <div role="alert" className="flex flex-wrap items-center justify-center gap-2 border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-xs font-medium text-destructive no-print">
      <MonitorPlay className="h-3.5 w-3.5 flex-none" aria-hidden />
      <span>
        The platform Owner ({watch.ownerName}) is watching your screen{controlled ? ' and controlling it' : ''} — {watch.reason}. Ends at {timeOf(watch.endsAt)}. Passwords and typed
        values are hidden from them.
      </span>
      {controlRequest && !controlled && (
        <span className="flex items-center gap-1.5">
          <span>{controlRequest} asks to control your screen.</span>
          <button type="button" className="rounded border border-destructive px-2 py-0.5 underline-offset-2 hover:bg-destructive/20" onClick={() => socket.current?.emit('control-answer', { accepted: true })}>
            Allow
          </button>
          <button type="button" className="underline underline-offset-2" onClick={() => (setControlRequest(null), socket.current?.emit('control-answer', { accepted: false }))}>
            Decline
          </button>
        </span>
      )}
      {controlled && (
        <button type="button" className="underline underline-offset-2" onClick={() => socket.current?.emit('control-release')}>
          Take back control
        </button>
      )}
      <button type="button" className="underline underline-offset-2" onClick={() => socket.current?.emit('end')}>
        Stop watching
      </button>
    </div>
  );
}
